import React, { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import ReactDOM from 'react-dom'
import { Redirect, Route } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import { createBrowserHistory } from 'history'
import { Provider, useSelector } from 'react-redux'
import { Switch, useLocation } from 'react-router'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { PostHogProvider } from '@posthog/react'
import { Toaster } from 'react-hot-toast'
import CanonicalLink from 'components/CanonicalLink'

import MySites from './Manage'
import { store } from './store'
import Login from './Login'
import ProWelcome from './ProWelcome'
import Auth from './Auth'
import Pricing from './Pricing'
import Help from './Help'
import Landing from './Landing'
import { STRIPE_KEY, reCAPTCHA_CLIENT, POSTHOG_KEY, POSTHOG_HOST } from './constants'
import NotFound from './components/NotFound'
import SupportUpload from './SupportUpload'
import ReportSite from './ReportSite'
import Affiliates from './Affiliates'
import Connect from './Connect'
import { isDevSite, isPreviewSite } from './utils/general'
import Examples from './Examples/Examples'
import FreeWelcome from './FreeWelcome'
import celloService from './services/cello'
import { persistUtmParamsFromUrl } from './utils/utm'
import { setupEnvAccessKey } from './utils/envAccessKey'
import SignUp from './sign-up/SignUp'
import Sales from './Sales'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import AdLanding from './Pages/AdLanding/AdLanding'
import CookieBanner from './components/CookieBanner'
import HostWithTiiny from './HostWithTiiny/HostWithTiiny'
import TransferRequestLinkUpload from 'components/TransferRequestLink/TransferRequestLinkUpload'
import TiinyTransfer from 'components/TiinyTransfer/TiinyTransfer'
import Creators from './Creators'
import Games from './Games'

import './index.css'

const allowedSentryEnvironments = new Set(['local', 'development', 'preview', 'production'])

function getSentryEnvironment() {
  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local'
  }

  if (isPreviewSite()) {
    return 'preview'
  }

  if (isDevSite()) {
    return 'development'
  }

  return 'production'
}

const sentryEnvironment = getSentryEnvironment()

if (!isDevSite()) {
  Sentry.init({
    dsn: 'https://e07d6e394f5b44e9a595102da16acb51@o383510.ingest.us.sentry.io/5213708',
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
    environment: sentryEnvironment,
    initialScope: {
      tags: {
        deployment: sentryEnvironment
      }
    },
    ignoreErrors: [
      "Failed to read the 'localStorage' property from 'Window'",
      "SecurityError: Failed to read the 'localStorage' property from 'Window'",
      /localStorage/i,
      'Network Error',
      "ReferenceError: Can't find variable: gmo",
      /Can't find variable: gmo/i,
      /Object Not Found Matching Id/i,
      /reCAPTCHA/i,
      'Timeout during Recaptcha execution',
      'Recaptcha verification failed',
      /reCAPTCHA Timeout/i,
      'Failed to load Stripe.js',
      'Stripe.js not available',
      'Stripe.js has not been loaded',
      'IntegrationError: stripe',
      /stripe\.js/i,
      "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
      /Failed to execute 'removeChild' on 'Node'/i,
      /The node to be removed is not a child of this node/i,
      'null is not an object (evaluating \'a.querySelector("#name").value\')',
      /evaluating 'a\.querySelector\("#name"\)\.value'/i,
      'Invalid sales form capture.',
      'Maximum call stack size exceeded',
      /Maximum call stack size exceeded/i
    ]
  })
}

function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

const stripePromise = loadStripe(STRIPE_KEY, {
  betas: ['elements_tax_id_1']
}).catch((err) => {
  window.stripeFailed = true
  window.dispatchEvent(new CustomEvent('stripe-load-error'))
  return null
})

const promoCodeCheck = async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('promoCode')
  const ref = urlParams.get('ref')
  const mobileMode = urlParams.get('m')

  if (code) {
    window.localStorage.setItem('promoCode', code)
  }

  if (mobileMode === 'ma') {
    window.localStorage.setItem('mobile-mode', 'true')
  }
}

function onClarityReady(callback, maxAttempts = 50) {
  let attempts = 0
  const interval = setInterval(() => {
    attempts++
    if (attempts >= maxAttempts) {
      clearInterval(interval)
      return
    }
    if (typeof window.clarity === 'function' && !window.clarity.toString().includes('.q||[]')) {
      clearInterval(interval)
      callback()
    }
  }, 200)
  return interval
}

const App = (props) => {
  const sentryEnvironment = getSentryEnvironment()
  const userProfile = useSelector((state) => state.manage?.userProfile)
  const userId = userProfile?.id

  useEffect(() => {
    let intervalId
    const sessionId = uuidv4()
    Sentry.setTag('tiinySessionId', sessionId)

    intervalId = onClarityReady(() => {
      window.clarity('set', 'tiinySessionId', sessionId)
      window.clarity('event', 'tag_set')
    })
    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    if (userId == null) return
    let intervalId = onClarityReady(() => {
      window.clarity('identify', userId)
    })
    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [userId])

  useEffect(() => {
    persistUtmParamsFromUrl()

    const urlParams = new URLSearchParams(window.location.search)
    const ucc = urlParams.get('ucc')
    if (ucc) {
      celloService.initUCC(ucc)
    }

    const unlisten = props.history.listen(() => {
      persistUtmParamsFromUrl()
    })

    if (process.env.REACT_APP_ENABLE_E2E === 'true') {
      document.body.classList.add('enable-e2e')
    }
    return () => {
      unlisten()
      document.body.classList.remove('enable-e2e')
    }
  }, [])

  promoCodeCheck()
  persistUtmParamsFromUrl()

  return (
    <ConnectedRouter history={props.history}>
      <Elements stripe={stripePromise}>
        <ScrollToTop />
        <div className="App">
          <Toaster position="top-left" toastOptions={{ duration: 25000 }} />
          <CanonicalLink history={props.history} />
          <Switch>
            <Route path="/" exact component={Landing} />
            <Route path="/transfer-request/:domain" component={TransferRequestLinkUpload} />
            <Route path="/send" component={TiinyTransfer} />
            <Route path="/login" exact component={Login} />
            <Route path="/auth/:id" exact component={Auth} />
            <Route path="/manage" component={MySites} />
            <Route path="/pro-welcome" component={ProWelcome} />
            <Route path="/free-welcome" component={FreeWelcome} />
            <Route path="/pricing" component={Pricing} />
            <Route path="/examples/:type?" component={Examples} />
            <Route path="/help" component={Help} />
            <Route path="/support-upload" component={SupportUpload} />
            <Route path="/report-site" component={ReportSite} />
            <Route path="/affiliates" component={Affiliates} />
            <Route path="/connect" component={Connect} />
            <Route path="/sign-up" component={SignUp} />
            <Route path="/contact-sales" component={Sales} />
            <Route path="/rl" component={AdLanding} />
            <Route path="/fl" component={AdLanding} />
            {/*<Route path="/deploy" component={HostWithTiiny} />*/}
            <Route path="/creators" component={Creators} />
            <Redirect exact from="/live" to="/live/games/arcade" />
            <Route path="/live/games/:category?" component={Games} />
            <Route component={NotFound} />
          </Switch>
          <CookieBanner />
        </div>
      </Elements>
    </ConnectedRouter>
  )
}

const history = createBrowserHistory()

// Dev / preview: render a blank page unless nginx accepts the env access key
setupEnvAccessKey().then((allowed) => {
  if (!allowed) return

  ReactDOM.render(
    <Provider store={store(history)}>
      <GoogleReCaptchaProvider reCaptchaKey={reCAPTCHA_CLIENT}>
        <PostHogProvider apiKey={POSTHOG_KEY} options={{ api_host: POSTHOG_HOST }}>
          <App history={history} />
        </PostHogProvider>
      </GoogleReCaptchaProvider>
    </Provider>,
    document.getElementById('root')
  )
})
